import { GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { isNil } from "@roc/feature-utils";
import { ComparableProperty } from "../types";
import * as uuid from 'uuid';
import { CompToolPhotosService } from "../services/compToolPhotosService";
import { formatDateField } from "@roc/feature-valuation-review-v2";

export class CompToolPhotosStore {
  globalStore: GlobalStore;
  compToolPhotosService: CompToolPhotosService;
  allPropertyPhotos: Record<string, any[]> = {};
  allPropertyPhotosGroupByDate: Record<string, any[]> = {};
  selectedPhoto: any;
  openIndividualPhoto: boolean

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.compToolPhotosService = new CompToolPhotosService();
    this.allPropertyPhotos = {};
    this.allPropertyPhotosGroupByDate = {};
    this.openIndividualPhoto = false;

    makeObservable(this, {
      fetchAllCompPhotos: flow,
      fetchAllCompPhotosGroupByDate: flow,
      allPropertyPhotos: observable,
      allPropertyPhotosGroupByDate: observable,
      selectedPhoto: observable,
      setSelectedPhoto: action,
      openIndividualPhoto: observable,
      setOpenIndividualPhoto: action
    });
  }

  reset() {
    this.allPropertyPhotosGroupByDate = {};
    this.allPropertyPhotos = {};
    this.selectedPhoto = [];
    this.openIndividualPhoto = false;
  }

  setOpenIndividualPhoto(openIndividualPhoto: boolean) {
    this.openIndividualPhoto = openIndividualPhoto;
  }

  setSelectedPhoto(selectedPhoto: any) {
    this.selectedPhoto = selectedPhoto;
  }


  *fetchAllCompPhotos(property) {
    try {

      const propertyId = this.getIdFromProperty(property);

      if (!propertyId) {
        return;
      }

      //we fetch only once
      if (this.allPropertyPhotos[propertyId]) {
        return;
      }

      let photosFromResponse = [];

      const mlsId = property?.data?.mls_id;
      if (mlsId) {
        const response = yield this.compToolPhotosService.fetchAllCompPhotos(mlsId);

        if (!response || response?.data?.status !== 'OK') {
          this.globalStore.notificationStore.showErrorNotification({
            message: 'There was an error getting the property photos.',
          });
          return;
        }

        const compImages = response.data && JSON.parse(response.data?.data);
        photosFromResponse = compImages?.images || [];
      }

      //if we get nothing on this response we use the photos from the comparable
      const photosToUse = photosFromResponse.length > 0 ? photosFromResponse : property?.data?.images || [];

      this.allPropertyPhotos[propertyId] = photosToUse.map(pi => ({
        key: uuid.v4(),
        url: pi.url
      }));
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error getting the property photos.',
      });
    }

  }

  *fetchAllCompPhotosGroupByDate(property: ComparableProperty) {
    try {
      const propertyId = this.getIdFromProperty(property);

      if (property.added_manually_comparable) {//we generate the id for these ones so we can't fetch the photos
        return;
      }

      if (!propertyId) {
        return;
      }

      //we fetch only once
      if (this.allPropertyPhotosGroupByDate[propertyId]) {
        return;
      }

      const response = yield this.compToolPhotosService.fetchAllCompPhotosGroupByDate(propertyId);
      if (!response || response?.data?.status !== 'OK') {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'There was an error getting the property photos.',
        });
        return;
      }

      const compImages = response.data && JSON.parse(response.data?.data);
      const listings = compImages?.listings || [];
      this.allPropertyPhotosGroupByDate[propertyId] = listings.sort((a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()).map(({ date, photos }) => ({
        date: formatDateField(date),
        photos: photos.map(url => ({
          key: uuid.v4(),
          url
        }))
      }));
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error getting the property photos.',
      });
    }
  }

  getPropertyPhotos(property: ComparableProperty) {
    const propertyId = this.getIdFromProperty(property);
    return this.allPropertyPhotos[propertyId] || [];
  }

  getPropertyPhotosGroupByDate(property: ComparableProperty) {
    const propertyId = this.getIdFromProperty(property);
    return this.allPropertyPhotosGroupByDate[propertyId] || [];
  }

  private getIdFromProperty(property: ComparableProperty) {
    if (!isNil(property.property_id)) {
      return property.property_id.split('_')[0];
    }
    return null;
  }
}
